/**
 * A video's best clips for the Strips rows and the quick look: the same shortlist the Top 20 page
 * uses (`clip-shortlist/<id>/`), a query with no rendering. Answers are kept for the page's life,
 * so scrolling back up or reopening a quick look doesn't ask again.
 */
import { useEffect, useState } from "react";
import apiRequest from "../../../../utils/apiClient";
import { clipShortlistUrl } from "../../../constants";
import { ReelClip, Recipe } from "../../../reel/hooks/useShortlist";

export type { ReelClip, Recipe };

interface Answer {
  clips: ReelClip[];
  recipes: Recipe[];
  videoUrl: string;
}

const cache = new Map<string, Answer>();

export const useTopClips = (
  transcriptId: number | null,
  limit: number,
  recipe = "longest_quotes",
  enabled = true,
  /** Changes when the clip pictures finish (thumbnails_ready), so an early answer without
   *  pictures is asked again instead of being kept for the page's life. */
  version = "",
): { data: Answer | null; loading: boolean; error: boolean } => {
  const key = `${transcriptId}|${limit}|${recipe}|${version}`;
  const [data, setData] = useState<Answer | null>(() => cache.get(key) || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!transcriptId || !enabled) return undefined;
    const hit = cache.get(key);
    if (hit) { setData(hit); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(false);
    apiRequest(`${clipShortlistUrl(transcriptId)}?recipe=${encodeURIComponent(recipe)}&limit=${limit}`, { method: "GET" })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const body = await res.json();
        const answer = { clips: body.clips || [], recipes: body.recipes || [], videoUrl: body.video_url || "" };
        cache.set(key, answer);
        if (!cancelled) setData(answer);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [key, transcriptId, limit, recipe, enabled]);

  return { data, loading, error };
};

/** 75.4 → "1:15", 3725 → "1:02:05". */
export const clock = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
};
